import React, { useState } from 'react';
import { X } from 'lucide-react';

interface RejectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: string, details: string) => void;
}

const REJECTION_REASONS = [
  'We decided to implement it in-house',
  'The price is too high compared to other estimates',
  'You are missing some features',
  'Other'
];

export function RejectionModal({ isOpen, onClose, onSubmit }: RejectionModalProps) {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [customDetails, setCustomDetails] = useState<string>('');
  const [showThankYou, setShowThankYou] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!selectedReason) return;

    const details = selectedReason === 'Other' ? customDetails : '';
    onSubmit(selectedReason, details);
    setShowThankYou(true);

    setTimeout(() => {
      setShowThankYou(false);
      setSelectedReason('');
      setCustomDetails('');
      onClose();
    }, 2000);
  };

  const handleClose = () => {
    setShowThankYou(false);
    setSelectedReason('');
    setCustomDetails('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {showThankYou ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Thank You for Your Feedback</h3>
            <p className="text-gray-600">We appreciate you taking the time to let us know.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Rejection Feedback</h2>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-gray-700 mb-4">
                We'd love to understand why you're declining this proposal. Your feedback helps us improve.
              </p>

              <div className="space-y-3">
                {REJECTION_REASONS.map((reason) => (
                  <label
                    key={reason}
                    className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <input
                      type="radio"
                      name="rejection_reason"
                      value={reason}
                      checked={selectedReason === reason}
                      onChange={(e) => setSelectedReason(e.target.value)}
                      className="mt-1 w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-gray-700 flex-1">{reason}</span>
                  </label>
                ))}
              </div>

              {selectedReason === 'Other' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Please provide more details
                  </label>
                  <textarea
                    value={customDetails}
                    onChange={(e) => setCustomDetails(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    placeholder="Tell us more about your decision..."
                  />
                </div>
              )}
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!selectedReason || (selectedReason === 'Other' && !customDetails.trim())}
                className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Submit Feedback
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
